import React, { type ReactNode } from 'react';
import { Box, LinearProgress, linearProgressClasses, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import hatchesImage from '../../assets/images/Hatches.png';

const stripeMove = keyframes`
    0%   { background-position: 0 0; }
    100% { background-position: 500px 0; }
`;

interface StripedLinearProgressProps {
    barHeight: number;
    bgColor: string;
}

const StripedLinearProgress = styled(LinearProgress, {
    shouldForwardProp: (prop) => !['barHeight', 'bgColor'].includes(prop as string),
})<StripedLinearProgressProps>(({ barHeight, bgColor }) => ({
    height: barHeight,
    borderRadius: 0,
    border: 0,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: bgColor,
        backgroundImage: `url(${hatchesImage})`,
        backgroundRepeat: 'repeat',
        backgroundSize: '500px 50px',
        animation: `${stripeMove} 3s linear infinite`,
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 0,
        backgroundColor: 'transparent',
    },
    [`& .${linearProgressClasses.bar1Indeterminate}`]: {
        display: 'none',
    },
    [`& .${linearProgressClasses.bar2Indeterminate}`]: {
        display: 'none',
    },
}));

export interface StripedProgressBarProps {
    value?: number;
    variant?: 'determinate' | 'indeterminate';
    height?: number;
    backgroundColor?: string;
    label?: ReactNode;
}

export const StripedProgressBar: React.FC<StripedProgressBarProps> = ({
    value,
    variant = 'indeterminate',
    height = 18,
    backgroundColor = '#F7F8F8',
    label,
}) => (
    <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
        <StripedLinearProgress
            variant={variant}
            value={variant === 'determinate' ? value : undefined}
            barHeight={height}
            bgColor={backgroundColor}
        />
        {label !== null && (
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                }}
            >
                {typeof label === 'string' ? (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: '#424E54',
                            fontFamily: '"Open Sans", sans-serif',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {label}
                    </Typography>
                ) : (
                    label
                )}
            </Box>
        )}
    </Box>
);
